/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { getByIdAsync } from "@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g";
import * as Msdyn365 from "@msdyn365-commerce/core";

import { lTSGetProductAssetsAsync } from "./AssetsDataActionExtension.g";
import {
  IAsset,
  ILTSGetProductAssetResponse,
} from "./AssetsDataServiceEntities.g";

export type { IAsset, ILTSGetProductAssetResponse };

export class BritaxGetAssetsInput implements Msdyn365.IActionInput {
  public readonly recordId: string;

  constructor(recordId: string) {
    this.recordId = recordId;
  }

  public getCacheKey = () => `assets`;
  public getCacheObjectType = () => "assets";
  public dataCacheType = (): Msdyn365.CacheType => "none";
}

function emptyProductAssetsResponse(): ILTSGetProductAssetResponse {
  return {
    TotalAssets: 0,
    Assets: [],
    AssetMetadataType: "",
    IsReleaseGroupLocked: false,
    FeatureFlags: undefined,
    SiteDisplayName: "",
    SupportedExtensions: [],
    IsVideoEnabled: false,
  };
}

const createBritaxGetAssetsInput = (
  args: Msdyn365.ICreateActionContext,
): Msdyn365.IActionInput => {
  const recordId = args.requestContext.urlTokens?.recordId ?? "";
  return new BritaxGetAssetsInput(String(recordId));
};

export async function getProductAssetsAction(
  input: BritaxGetAssetsInput,
  ctx: Msdyn365.IActionContext,
): Promise<ILTSGetProductAssetResponse> {
  const recordId =
    input.recordId || ctx.requestContext.urlTokens?.recordId || "";
  if (!recordId) {
    return emptyProductAssetsResponse();
  }

  const pID = parseInt(String(recordId), 10);
  if (Number.isNaN(pID)) {
    return emptyProductAssetsResponse();
  }

  const res = await getByIdAsync(
    { callerContext: ctx, bypassCache: "none" },
    pID,
    ctx.requestContext.apiSettings.channelId,
  );
  const baseProduct = Array.isArray(res) ? res[0] : res;
  const itemId = baseProduct?.ItemId;
  if (!itemId) {
    return emptyProductAssetsResponse();
  }

  return lTSGetProductAssetsAsync({ callerContext: ctx }, itemId);
}

export default Msdyn365.createObservableDataAction({
  action: <Msdyn365.IAction<ILTSGetProductAssetResponse>>getProductAssetsAction,
  id: "BritaxGetAssets",
  input: createBritaxGetAssetsInput,
});
